import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { ComponentLike } from '@glint/template';
import DOMPurify from 'dompurify';

import type { APIData, Service } from 'webapp-types';
import { TimeUnit } from 'webapp/enums/enums';
import { InsightType } from 'webapp/enums/metric-insights';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceInsightsSvgAvgDecreaseComponent from 'webapp/components/data-performance/insights/svg/avg-decrease';
import DataPerformanceInsightsSvgAvgIncreaseComponent from 'webapp/components/data-performance/insights/svg/avg-increase';
import DataPerformanceInsightsSvgHighestComponent from 'webapp/components/data-performance/insights/svg/highest';
import DataPerformanceInsightsSvgLowestComponent from 'webapp/components/data-performance/insights/svg/lowest';
import ToolTipComponent from 'webapp/components/tool-tip';

import DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type { ColoredMetricSetupData } from 'webapp/services/metric-details';

interface InsightDetails {
	metricName: string;
	text: string;
	class: string;
	source?: {
		id: string;
		imgSrc: string;
		imgAlt: string;
		title: string;
	};
	svgComponent?: ComponentLike;
	iconName?: IconName;
	colorIndex?: number;
}

type InsightDetailsSafe = Omit<InsightDetails, 'text'> & {
	text: SafeString;
};

type SafeInsight = APIData.MetricInsights.Insight & InsightDetailsSafe;

const enum AvgDirection {
	DECREASE = 'decrease',
	INCREASE = 'increase',
}

export interface Args {
	model?: APIData.MetricInsights.Insight[]; // Not passed for loading screens
	coloredMetricSetupDataItems?: ColoredMetricSetupData[]; // Passed only for metric-insights page InsightType
	timeUnit?: TimeUnit | null; // Passed only for metric-insights page InsightType that have granulation
	initiallyShownItems?: number; // Passed only for metric-insights page
}

export default class DataPerformanceInsightsSanitizedComponent extends Component<Args> {
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked visibleInsightsCount: number | null = null; // show all when null

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.areInsightsCollapsible) {
			this.visibleInsightsCount = this.args.initiallyShownItems!;
		}
	}

	get isLoading() {
		return this.args.model === undefined;
	}

	private static loadingFakeInsights = [
		{
			type: InsightType.HIGHEST_VALUE,
			metricSetup: { key: '123|abc', spaceAccessId: 123 },
			value: '10',
		},
		{
			type: InsightType.LOWEST_VALUE,
			metricSetup: { key: '123|abc', spaceAccessId: 123 },
			value: '9',
		},
		{ type: InsightType.AVG, metricSetup: { key: '123|abc', spaceAccessId: 123 }, value: '8' },
	];

	get model() {
		return this.isLoading
			? DataPerformanceInsightsSanitizedComponent.loadingFakeInsights
			: this.args.model || [];
	}

	get timeUnitTextShort() {
		const timeUnit = this.args.timeUnit;
		return timeUnit ? DateRange.getTimeUnitText(timeUnit) : '';
	}

	get timeUnitText() {
		const timeUnit = this.args.timeUnit;
		return timeUnit ? DateRange.getTimeUnitText(timeUnit, true) : '';
	}

	@cached
	get sanitizedInsights() {
		const sanitizedInsights = this.model.map((insight) => {
			const insightData = this.getInsightDetails(
					insight,
					this.args.coloredMetricSetupDataItems || [],
				),
				text = htmlSafe(
					DOMPurify.sanitize(insightData.text, {
						ALLOWED_TAGS: ['div', 'p', 'b', 'span'],
						ALLOWED_ATTR: ['class', 'style'],
					}),
				),
				metricName = insightData.metricName;

			const sanitizedInsight: SafeInsight = {
				metricName,
				...insight,
				text,
				class: insightData.class,
				source: insightData.source,
				svgComponent: insightData.svgComponent,
				iconName: insightData.iconName,
			};

			return sanitizedInsight;
		});

		return sanitizedInsights;
	}

	getInsightDetails(
		insight: APIData.MetricInsights.Insight,
		coloredMetricSetupDataItems: ColoredMetricSetupData[],
	) {
		const insightData: InsightDetails = {
			metricName: '',
			text: '',
			class: '',
		};

		const showMetrickeyName = !insight.type.includes('dimensional');

		const metricSetup = new MetricSetup(insight.metricSetup || {});

		const metric = metricSetup.metric;

		let metricName = '';
		if (showMetrickeyName) {
			if (metric) {
				metricName = metricSetup.selectedMetricTitleWithUnit;

				const sourceCM = metric.source;
				if (sourceCM) {
					insightData.source = {
						id: sourceCM.id!,
						imgSrc: sourceCM.realAvatar,
						imgAlt: sourceCM.sourceType.name,
						title: sourceCM.title,
					};
				}
			} else {
				metricName = insight.metricSetup.key;
			}
		} else {
			metricName = metricSetup.selectedAttributesAndUnit;
		}

		switch (insight.type) {
			case InsightType.POSITIVE:
			case InsightType.NEGATIVE: {
				let text = '';

				if (metric) {
					insightData.class =
						(insight.type === InsightType.POSITIVE) === metric.changeInvert
							? 'lowest'
							: 'highest';
					insightData.iconName =
						insightData.class === 'highest' ? 'TrendUp' : 'TrendDown';
					text += `${insightData.class} `;
				} else {
					insightData.class = 'unknown';
				}

				text += `value of ${metricName} was <b>${insight.value}</b>`;

				if (insight.date) {
					text += ` on ${insight.date}`;
				}

				insightData.text = capitalize(text);
				break;
			}
			case InsightType.AVG: {
				let timeUnitTextShort = this.timeUnitTextShort;
				if (!timeUnitTextShort) {
					timeUnitTextShort = DateRange.getTimeUnitText(TimeUnit.DAY); // For performance screen, timeunit is always day and not passed as attribute
				}
				insightData.class = 'average';
				insightData.text = `On average, you had <b>${insight.value}</b> <span class="in-own-line">${metricName} per ${timeUnitTextShort}</span>`;
				insightData.iconName = 'TrendAverage';

				break;
			}
			case InsightType.HIGHEST_VALUE:
			case InsightType.LOWEST_VALUE: {
				if (metric) {
					insightData.class =
						(insight.type === InsightType.HIGHEST_VALUE) === metric.changeInvert
							? 'negative'
							: 'positive';
				} else {
					insightData.class = 'unknown';
				}
				insightData.svgComponent =
					insight.type === InsightType.HIGHEST_VALUE
						? DataPerformanceInsightsSvgHighestComponent
						: DataPerformanceInsightsSvgLowestComponent;

				let text = `${insight.type.split('-').join(' ')} of <b>${insight.value}</b>`;

				if (insight.date) {
					text += `<span class="in-own-line">${insight.date}</span>`;
				}

				insightData.text = capitalize(text);
				break;
			}
			case InsightType.ABSOLUTE_CHANGE_PREVIOUS_PERIOD:
			case InsightType.PERCENT_CHANGE_LAST_YEAR: {
				const direction = insight.value.startsWith('-')
						? AvgDirection.DECREASE
						: AvgDirection.INCREASE,
					cleanValue = insight.value.replace('-', ''),
					isPreviousPeriod = insight.type === InsightType.ABSOLUTE_CHANGE_PREVIOUS_PERIOD,
					timeUnitText = this.timeUnitText,
					timeUnitTextShort = this.timeUnitTextShort;

				if (metric) {
					insightData.class =
						(direction === AvgDirection.INCREASE) === metric.changeInvert
							? 'negative'
							: 'positive';
				} else {
					insightData.class = 'unknown';
				}
				insightData.svgComponent =
					direction === AvgDirection.INCREASE
						? DataPerformanceInsightsSvgAvgIncreaseComponent
						: DataPerformanceInsightsSvgAvgDecreaseComponent;

				const indicatorClasses = 'indicator ' + insightData.class;

				insightData.text = `
					You
					${
						isPreviousPeriod
							? '<b class="direction">' + direction + 'd' + '</b>'
							: 'have a <span class="value-change"><span class="' +
								indicatorClasses +
								'">' +
								cleanValue +
								' ' +
								direction +
								'</span></span>  in'
					}
					 average ${timeUnitText ? timeUnitText + ' ' : ''}<b>${metricName}</b>
					 ${
							isPreviousPeriod
								? 'for <span class="value-change"><span class="' +
									indicatorClasses +
									'">' +
									cleanValue +
									'</span></span> ' +
									metricName +
									(timeUnitTextShort ? '/' + timeUnitTextShort : '')
								: ''
						} compared to the
					${isPreviousPeriod ? 'previous period' : 'same period last year'}
				`;

				break;
			}
			case InsightType.DIMENSIONAL_POSITIVE_CHANGE_HIGHEST:
			case InsightType.DIMENSIONAL_NEGATIVE_CHANGE_HIGHEST:
			case InsightType.DIMENSIONAL_POSITIVE_CHANGE_LOWEST:
			case InsightType.DIMENSIONAL_AVERAGE_HIGHEST:
			case InsightType.DIMENSIONAL_AVERAGE_LOWEST: {
				const isAverage = insight.type.includes('average'),
					isHighest = insight.type.includes('highest'),
					isChangeNegativeNumber = insight.value.startsWith('-'), // refers to the value number sign. Could also be derived as [insight.type.includes('negative')]
					cleanValue = insight.value.replace('-', ''),
					timeUnitText = this.timeUnitText,
					timeUnitTextShort = this.timeUnitTextShort;

				const colorHex = metricSetup.key
					? this.metricDetailsService.getColorHexForSelection(
							metricSetup,
							coloredMetricSetupDataItems,
						)
					: '';

				insightData.class = 'unknown';

				let text = `<div class="dimension-name-wrapper"><b class="dimension-name" style="color: ${colorHex}">${metricName}</b></div>`;

				if (isAverage) {
					const isChangeNegative = isHighest === metric?.changeInvert, // refers to whether this value is desired or not for the metric type. Can be different from isChangeNegativeNumber
						direction = isChangeNegativeNumber
							? AvgDirection.DECREASE
							: AvgDirection.INCREASE;

					if (metric) {
						insightData.class = isChangeNegative ? 'negative' : 'positive';
					}

					insightData.svgComponent =
						direction === AvgDirection.INCREASE
							? DataPerformanceInsightsSvgAvgIncreaseComponent
							: DataPerformanceInsightsSvgAvgDecreaseComponent;

					text += ` had the ${isChangeNegative ? 'worst' : 'best'} average ${timeUnitText ? timeUnitText + ' ' : ''}results with <b>${insight.value}</b> per ${timeUnitTextShort}.`;
				} else {
					if (metric) {
						insightData.class =
							(isChangeNegativeNumber === isHighest) === metric?.changeInvert
								? 'positive'
								: 'negative';
					}

					const indicatorClasses = 'indicator ' + insightData.class,
						changePrefix = isChangeNegativeNumber ? '▼' : '▲',
						valueWithArrow = changePrefix + ' ' + cleanValue;

					insightData.class += isChangeNegativeNumber ? ' lowest' : ' highest'; // Icon class. Append after indicatorClasses is set.
					insightData.iconName = isChangeNegativeNumber ? 'TrendDown' : 'TrendUp';

					text += ` ${isChangeNegativeNumber ? 'decreased' : 'grew'} the ${isHighest ? 'most' : 'least'} with
					<span class="value-change"><span class="${indicatorClasses}">${valueWithArrow}</span></span> compared to the previous period`;
				}

				insightData.text = text;

				break;
			}
			default:
				break;
		}

		insightData.metricName = metricName;

		return insightData;
	}

	get visibleInsights() {
		return (this.sanitizedInsights || []).slice(0, this.maxVisibleInsightsCount);
	}

	get summarySuggestionsLength() {
		return this.sanitizedInsights.length;
	}

	get maxVisibleInsightsCount() {
		return this.visibleInsightsCount === null
			? this.summarySuggestionsLength
			: Math.min(this.visibleInsightsCount, this.summarySuggestionsLength);
	}

	get hasHiddenInsights() {
		return this.maxVisibleInsightsCount < this.summarySuggestionsLength;
	}

	get areInsightsCollapsible() {
		return (
			typeof this.args.initiallyShownItems === 'number' &&
			this.args.initiallyShownItems < this.summarySuggestionsLength
		);
	}

	@action
	showMoreInsights() {
		this.visibleInsightsCount = null;
	}

	@action
	showLessInsights() {
		if (this.areInsightsCollapsible) {
			this.visibleInsightsCount = this.args.initiallyShownItems!;
		}
	}
	<template>
		<div class="metric-insights-list">
			<ul>
				{{#each this.visibleInsights as |insight|}}
					<li
						class={{joinClasses
							(array insight.type insight.class (if this.isLoading "loading"))
						}}
					>
						<div class={{joinClasses (array "icon" (if insight.svgComponent "svg"))}}>
							{{#if insight.svgComponent}}
								{{#let (component insight.svgComponent) as |SvgComponent|}}
									<SvgComponent />
								{{/let}}
							{{else if insight.iconName}}
								<CommonUiIconComponent @name={{insight.iconName}} @size="md" />
							{{/if}}
						</div>
						{{#if this.isLoading}}
							<div class="li-content"></div>
						{{else}}
							<div class="li-content">
								<p>{{insight.text}}</p>
								{{#if insight.source.id}}
									<div class="source">
										<img
											src={{insight.source.imgSrc}}
											width="16"
											height="16"
											alt={{insight.source.imgAlt}}
										/>
										<p class="source-title ellipsis-overflow">
											{{insight.source.title}}
											<ToolTipComponent @text={{insight.source.title}} />
										</p>
									</div>
								{{/if}}
							</div>
						{{/if}}
					</li>
				{{/each}}
			</ul>

			{{#if this.areInsightsCollapsible}}
				{{#if this.hasHiddenInsights}}
					<div role="button" class="view-more-btn" {{on "click" this.showMoreInsights}}>
						View more<CommonUiIconComponent @name="ChevronDown" @size="sm" />
					</div>
				{{else}}
					<div role="button" class="view-more-btn" {{on "click" this.showLessInsights}}>
						View less<CommonUiIconComponent @name="ChevronUp" @size="sm" />
					</div>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
